import React, { useMemo, Fragment, useRef } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages } from 'react-intl';
import { FormattedMessageWrappedInSpan } from '../components/misc';
import includes from 'lodash.includes';
import { useHistory, Redirect } from 'react-router-dom';

import KeyboardBackspaceRoundedIcon from '@material-ui/icons/KeyboardBackspaceRounded';

import MediaList from '../components/content/lists/MediaList';
import PhotoBookInfoBanner from '../components/content/photo-book/PhotoBookInfoBanner';
import AccountDownloadTile from '../components/content/account/AccountDownloadTile';
import StillProcessingImagesInfo from '../components/content/informations/StillProcessingImagesInfo';
import NoPhotosForOrderActions from '../components/content/account/NoPhotosForOrderActions';
import SectionHeader from '../components/misc/SectionHeader';
import EventBar from '../components/content/event/EventBar';
import {
  customScrollToComponent,
  getCertificate,
  includesCertificate,
  maybeExtractCombinedBib,
  includesPhotoBook,
  getVideo,
  includesVideo,
  includesFotoFlat
} from '../utils/common';
import {
  ORIGINAL,
  SOCIAL_MEDIA,
  SINGLE_PHOTO,
  VIDEO,
  CERTIFICATE,
  VOUCHER,
  MAIN_SEARCH_FACE,
  RESTART_STATE_CAN_RESTART,
  RESTART_STATE_PROCESSING,
  RESTART_PRODUCTION,
  SINGLE_MEDIA_ACCOUNT_PAGE,
  ACCOUNT,
  CERTIFICATE_WITH_USER_INPUT,
  CERTIFICATE_WITH_ADDITIONAL_CONSENT,
} from '../utils/variables';

const AccountEventPage = (props) => {
  const { intl, viewer, match } = props;
  const { account } = viewer;
  const bibNumberForPage = match.params.bibnumber;
  const [dataForBibNumber] = account.eventPurchases.participants.filter(
    (eventPurchaseInfo) => eventPurchaseInfo.startnumber === bibNumberForPage
  );
  const isSingleMediaPage = bibNumberForPage === SINGLE_MEDIA_ACCOUNT_PAGE;
  const availableZips = viewer.account.eventPurchases.availableZips ?? [];
  const restartState = account.eventPurchases.restartState;
  const originalsAreAvailable = includes(availableZips, ORIGINAL);
  const socialMediaAreAvailable = includes(availableZips, SOCIAL_MEDIA);
  const isFaceSearchEvent = viewer.account.boughtEvent.mainSearch === MAIN_SEARCH_FACE;
  const hasVideo = includesVideo(viewer.account.boughtEvent.products);
  const hasCertificates = includesCertificate(viewer.account.boughtEvent.products);
  const hasBoughtFotoFlat =
    includesFotoFlat(viewer.account.boughtEvent.products) &&
    availableZips &&
    availableZips.some((value) => value !== ORIGINAL && value !== SOCIAL_MEDIA);
  const videoReadyForDownload = hasVideo
    ? getVideo(viewer.account.boughtEvent.products).price.amount == 0
    : null;
  const hasPhotoBook = includesPhotoBook(viewer.account.boughtEvent.products);
  const mediaListRef = useRef(null);
  const stillProcessingInfoRef = useRef(null);
  const hasNoMediaForOrder =
    !isSingleMediaPage &&
    (!dataForBibNumber || dataForBibNumber.media.mediaInfo.count === 0);
  const isUserInputForCertificateRequired = useMemo(() => {
    return (
      (hasCertificates &&
        Boolean(
          getCertificate(viewer.account.boughtEvent.products).configuration
            ?.specialType === CERTIFICATE_WITH_USER_INPUT
        )) ||
      false
    );
  }, [viewer, hasCertificates]);
  const certificateOrganizerUrl = useMemo(() => {
    return (
      (hasCertificates &&
        getCertificate(viewer.account.boughtEvent.products).configuration
          ?.organizerUrl) ||
      ''
    );
  }, [viewer, hasCertificates]);

  const isAdditionalConsentForCertificateRequired = useMemo(() => {
    return (
      (hasCertificates &&
        Boolean(
          getCertificate(viewer.account.boughtEvent.products).configuration
            ?.specialType === CERTIFICATE_WITH_ADDITIONAL_CONSENT
        )) ||
      false
    );
  }, [viewer, hasCertificates]);

  const history = useHistory();

  const findEventVouchers = () => {
    const filteredOrders = account.orders?.edges.filter(
      (order) =>
        order.node.cart.lineItems.some(
          (item) => item.product.event.sgId === viewer.account.boughtEvent.sgId
        ) && order.node.vouchers.length > 0
    );
    return filteredOrders?.map((order) => ({
      vouchers: order.node.vouchers,
      orderid: order.node.orderid,
    }));
  };
  const vouchersForEvent = findEventVouchers();

  const handleCustomizePhotoBookClick = () => {
    const { history, match } = props;
    history.push(`/account/event/${match.params.id}/book`);
  };

  const scrollToFotoFlat = () => {
    if (stillProcessingInfoRef && stillProcessingInfoRef.current) {
      customScrollToComponent({ currentRef: stillProcessingInfoRef.current });
    } else {
      customScrollToComponent({ currentRef: mediaListRef.current });
    }
  };

  if (
    (!dataForBibNumber && !isSingleMediaPage) ||
    (isSingleMediaPage && account.eventPurchases.singleMedia.mediaInfo.count === 0)
  ) {
    // check if user didn't buy anything for that event+bib
    return <Redirect to="/account/events" />;
  }

  return (
    <div className="container-960 container-page account-event-page">
      <EventBar event={viewer.account.boughtEvent} />
      <div className="account-event-header">
        <div className="row account-event-info-card">
          <div className="col-xs-16 col-sm-12 col-sm-offset-2">
            <div
              className="navigation-back event-info-card-back"
              onClick={() => history.push('/account/events')}
            >
              <KeyboardBackspaceRoundedIcon />
              <FormattedMessageWrappedInSpan
                id="eventInfoCard.backToYourEventsButton"
                defaultMessage="Back to your events"
              />
            </div>
            <SectionHeader className="mt-20 mb-40">
              <FormattedMessageWrappedInSpan
                id="accountEventPage.products.title"
                defaultMessage="Download area"
                className="typography bold-text uppercase typography-title-medium"
              />
            </SectionHeader>
            {hasNoMediaForOrder && (
              <div className="col-sm-12 col-sm-offset-2">
                <NoPhotosForOrderActions
                  userInfo={viewer.account}
                  eventInfo={viewer.account.boughtEvent}
                  startNumber={bibNumberForPage}
                />
              </div>
            )}
            {!hasNoMediaForOrder && (
              <div className="account-download-container">
                <div className="account-download-body">
                  <div className="account-download-btns">
                    <AccountDownloadTile
                      type={SINGLE_PHOTO}
                      onScrollToMedia={scrollToFotoFlat}
                    />
                    {socialMediaAreAvailable && (
                      <AccountDownloadTile
                        eventSgId={viewer.account.boughtEvent.sgId}
                        type={SOCIAL_MEDIA}
                        values={SOCIAL_MEDIA}
                        bibNumber={bibNumberForPage}
                      />
                    )}
                    {(originalsAreAvailable ||
                      restartState === RESTART_STATE_PROCESSING) && (
                      <AccountDownloadTile
                        type={ORIGINAL}
                        values={ORIGINAL}
                        eventSgId={viewer.account.boughtEvent.sgId}
                        areProcessed={restartState === RESTART_STATE_PROCESSING}
                        bibNumber={bibNumberForPage}
                      />
                    )}
                    {hasCertificates && hasBoughtFotoFlat && !isSingleMediaPage && (
                      <AccountDownloadTile
                        type={CERTIFICATE}
                        onScrollToMedia={scrollToFotoFlat}
                        notActive={
                          !originalsAreAvailable ||
                          account.eventPurchases.availableCertificates.length === 0
                        }
                        originalsAreAvailable={originalsAreAvailable}
                        isFaceSearchEvent={isFaceSearchEvent}
                        startNumbers={account.eventPurchases.availableCertificates}
                        isAdditionalConsentForCertificateRequired={
                          isAdditionalConsentForCertificateRequired
                        }
                        certificateOrganizerUrl={certificateOrganizerUrl}
                      />
                    )}
                    {hasVideo && hasBoughtFotoFlat && !isSingleMediaPage && (
                      <AccountDownloadTile
                        values={bibNumberForPage}
                        type={VIDEO}
                        notActive={!videoReadyForDownload}
                        isFaceSearchEvent={isFaceSearchEvent}
                        eventSgId={viewer.account.boughtEvent.sgId}
                        intl={intl}
                      />
                    )}
                    {vouchersForEvent && vouchersForEvent.length > 0 && (
                      <AccountDownloadTile
                        type={VOUCHER}
                        values={[...vouchersForEvent]}
                      />
                    )}
                    {(!originalsAreAvailable || isSingleMediaPage) &&
                      restartState === RESTART_STATE_CAN_RESTART && (
                        <AccountDownloadTile
                          type={RESTART_PRODUCTION}
                          eventSgId={viewer.account.boughtEvent.sgId}
                        />
                      )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {hasPhotoBook && (
          <div className="row">
            <div className="col-xs-16 col-sm-12 col-sm-offset-2">
              <PhotoBookInfoBanner
                eventId={viewer.account.boughtEvent.sgId}
                eventName={viewer.account.boughtEvent.name}
                onContinueClick={handleCustomizePhotoBookClick}
              />
            </div>
          </div>
        )}
      </div>
      {viewer.account.boughtEvent.pendingMediaProcessing && (
        <div className="row">
          <div
            ref={stillProcessingInfoRef}
            className="col-xs-16 col-sm-12 col-sm-offset-2"
          >
            <StillProcessingImagesInfo version={ACCOUNT} />
          </div>
        </div>
      )}
      <div ref={mediaListRef}>
        {hasBoughtFotoFlat && !isSingleMediaPage && !hasNoMediaForOrder && (
          <>
            <div className="row">
              <div className="col-xs-16 col-sm-12 col-sm-offset-2">
                <SectionHeader className="mt-20 mb-40">
                  <span className="typography bold-text uppercase typography-title-medium">
                    {viewer.account.boughtEvent.mainSearch === MAIN_SEARCH_FACE
                      ? intl.formatMessage(translations.boughtPhotosFaceEvent, {
                          count: dataForBibNumber.media.mediaInfo.count,
                        })
                      : intl.formatMessage(translations.boughtPhotos, {
                          count: dataForBibNumber.media.mediaInfo.count,
                          startNumber: maybeExtractCombinedBib(dataForBibNumber.startnumber),
                          span: (chunks) => (
                            <span className="typography no-uppercase">{chunks}</span>
                          ),
                        })}
                    {viewer.account.boughtEvent.mainSearch === MAIN_SEARCH_FACE && (
                      <span>
                        {intl.formatMessage(translations.boughtPhotosFFVirtualId, {
                          virtualStartNumber: dataForBibNumber.startnumber,
                          span: (chunks) => (
                            <span className="typography no-uppercase">{chunks}</span>
                          ),
                        })}
                      </span>
                    )}
                  </span>
                </SectionHeader>
              </div>
            </div>
            <MediaList
              media={dataForBibNumber.media.mediaList}
              offer={null}
              eventSgId={viewer.account.boughtEvent.sgId}
              startNumbers={account.eventPurchases.availableCertificates}
              listType="account"
              restartState={restartState}
              isFaceSearchEvent={isFaceSearchEvent}
              hasCertificates={hasCertificates}
              hasBoughtFotoFlat={hasBoughtFotoFlat}
              isUserInputForCertificateRequired={isUserInputForCertificateRequired}
              isAdditionalConsentForCertificateRequired={
                isAdditionalConsentForCertificateRequired
              }
              certificateOrganizerUrl={certificateOrganizerUrl}
            />
          </>
        )}
      </div>
      {account.eventPurchases.singleMedia.mediaInfo.count > 0 && isSingleMediaPage && (
        <Fragment>
          <div className="row">
            <div className="col-xs-16 col-sm-12 col-sm-offset-2">
              <h1 className="separator">
                {intl.formatMessage(translations.singleMedia, {
                  count: account.eventPurchases.singleMedia.mediaInfo.count,
                })}
              </h1>
            </div>
          </div>
          <MediaList
            media={account.eventPurchases.singleMedia.mediaList}
            offer={null}
            eventSgId={viewer.account.boughtEvent.sgId}
            startNumbers={account.eventPurchases.availableCertificates}
            listType="account"
            restartState={restartState}
            isFaceSearchEvent={isFaceSearchEvent}
            hasCertificates={hasCertificates}
            hasBoughtFotoFlat={hasBoughtFotoFlat}
          />
        </Fragment>
      )}
      {account.eventPurchases.bonusMedia.mediaInfo.count > 0 && !isSingleMediaPage && (
        <Fragment>
          <div className="row">
            <div className="col-xs-16 col-sm-12 col-sm-offset-2">
              <h1 className="separator">
                {intl.formatMessage(translations.freePhotos, {
                  count: account.eventPurchases.bonusMedia.mediaInfo.count,
                })}
              </h1>
            </div>
          </div>
          <MediaList
            media={account.eventPurchases.bonusMedia.mediaList}
            eventSgId={viewer.account.boughtEvent.sgId}
            offer={null}
            listType="account"
            restartState={account.eventPurchases.restartState}
          />
        </Fragment>
      )}
    </div>
  );
};

const translations = defineMessages({
  boughtPhotos: {
    id: 'accountEventPage.boughtPhotos.uppercaseVersion',
    defaultMessage:
      'Listing {count, plural, one {# photo} other {# photos}} for bib number <span>{startNumber}</span>',
  },
  boughtPhotosFaceEvent: {
    id: 'accountEventPage.boughtPhotosFaceEvent',
    defaultMessage: 'Listing {count, plural, one {# photo} other {# photos}} you bought',
  },
  boughtPhotosFFVirtualId: {
    id: 'accountEventPage.boughtPhotosFFVirtualId.uppercaseVersion',
    defaultMessage: 'Foto-Flat ID <span>{virtualStartNumber}</span>',
  },
  singleMedia: {
    id: 'accountEventPage.singleMedia',
    defaultMessage:
      'Listing {count, plural, one {# photo} other {# photos}} you bought for this event',
  },
  freePhotos: {
    id: 'accountEventPage.freePhotos',
    defaultMessage:
      'Listing {count, plural, one {# free photo} other {# free photos}} for this event',
  },
  requestVideoButton: {
    id: 'accountEventPage.requestVideoButton.uppercaseVersion',
    defaultMessage: 'Generate video for bib number <span>{startNumber}</span>',
  },
  downloadVideoButton: {
    id: 'accountEventPage.downloadVideoButton.uppercaseVersion',
    defaultMessage: 'Download video for bib number <span>{startNumber}</span>',
  },
});

AccountEventPage.propTypes = {
  intl: PropTypes.object,
  viewer: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
};

export { AccountEventPage };

export default createFragmentContainer(injectIntl(AccountEventPage), {
  viewer: graphql`
    fragment AccountEventPage_viewer on Viewer {
      account {
        orders(first: 100) {
          edges {
            node {
              id
              cart {
                lineItems {
                  product {
                    event {
                      name
                      sgId
                    }
                  }
                }
              }
              orderid
              vouchers {
                id
                code
                campaign {
                  id
                  banner
                  url
                }
              }
            }
          }
        }
        boughtEvent(eventId: $eventId, language: $language) {
          ...EventBar_event
          name
          products {
            type
            price
            configuration {
              ... on EventCertificateConfiguration {
                specialType
                organizerUrl
              }
            }
          }
          sgId
          mainSearch
          pendingMediaProcessing
          ...NoPhotosForOrderActions_eventInfo
        }
        eventPurchases(eventId: $eventId) {
          availableCertificates
          availableVideos
          availableZips
          bonusMedia {
            mediaInfo {
              count
            }
            mediaList(first: 1000) {
              edges {
                node {
                  id
                  sgId
                  variants {
                    format
                    name
                    url
                  }
                }
              }
              ...MediaList_media
            }
          }
          canRestart
          restartState
          participants {
            media {
              mediaInfo {
                count
              }
              mediaList(first: 1000) {
                edges {
                  node {
                    id
                    sgId
                    variants {
                      format
                      name
                      url
                    }
                  }
                }
                ...MediaList_media
              }
            }
            startnumber
          }
          singleMedia {
            mediaInfo {
              count
            }
            mediaList(first: 1000) {
              edges {
                node {
                  id
                  sgId
                  variants {
                    format
                    name
                    url
                  }
                }
              }
              ...MediaList_media
            }
          }
        }
        ...NoPhotosForOrderActions_userInfo
      }
    }
  `,
});
